/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Divider,
} from '@heroui/react';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaMoneyBillWave,
  FaCheck,
  FaCreditCard,
} from 'react-icons/fa6';
import { FEATURE_FLAGS, useFeatureFlag } from '../../../config/featureFlags';
import CustomButton from '../../components/CustomButton';
import CustomAlert from '../../components/CustomAlert';
import SmallLoadingSpinner from '../../components/common/ui/SmallLoadingSpinner';
import { usePurchasePackage } from '../../../hook/useMutationData';
import { getMyPackages } from '../../../service/packageService';
import { formatCurrency } from '../../../utils/currency';
import { CustomerPackage, PackageTier } from '../../../utils/type';
import { all_routes } from '../../../core/data/routes/all_routes';
import PaymentSection, {
  STRIPE_PUBLISHABLE_KEY,
} from '../Booking/PaymentSection';

interface CheckoutSelectedAddOn {
  additionalServiceId: string;
  serviceItem: string;
  price: number;
  sessionNumbers: number[];
}

interface CheckoutState {
  serviceId?: string;
  serviceTitle?: string;
  tier?: PackageTier;
  // Phase 3.1 — per-session additional-service selection made on
  // PackageSelection. `sessionAddOns` is the flat payload shape the purchase
  // API expects; `selectedAddOns` carries the resolved name/price/session
  // list alongside it purely for rendering the breakdown below (this page
  // never re-fetches the service).
  sessionAddOns?: Array<{
    additionalServiceId: string;
    sessionNumbers: number[];
  }>;
  selectedAddOns?: CheckoutSelectedAddOn[];
  addonSubtotal?: number;
}

/**
 * Matches the backend bookingModel.js `paymentMethod` enum exactly:
 * 'Wallet' | 'Cash on Hand' | 'Credit/Debit Card' | 'Stripe' | … . Sent as a
 * record-keeping label only when the customer chooses Cash on Hand — the
 * backend never charges based on it.
 */
const PAYMENT_METHOD_CASH_ON_HAND = 'Cash on Hand';

type SelectedMethod = 'online' | 'cash';
type Phase = 'review' | 'paying' | 'confirming' | 'confirmed' | 'failed';

/** Stripe returns the customer here after a 3DS challenge. */
const RETURN_URL = `${window.location.origin}/service/package/checkout`;

/** Identifiers persisted so the flow can resume after a 3DS redirect. */
const PACKAGE_PAYMENT_RESUME_KEY = 'package.paymentResume';

const CONFIRM_TIMEOUT_MS = 30000;
const CONFIRM_POLL_MS = 1500;

/**
 * Phase 3 / 3.2 — Package Checkout.
 * Route: /service/package/checkout
 *
 * Price breakdown (tier price + any per-session additional-service subtotal,
 * see PackageSelection's SessionAddOnSelector, + tax placeholder), a choice
 * of Cash on Hand (settled offline, package stays paymentStatus 'PENDING')
 * or Pay Online (real Stripe PaymentIntent via gigmosaic-payment-service —
 * PackagePaymentService.js on the backend; this page polls
 * GET /customer-package/customer until the created package's paymentStatus
 * flips to 'PAID'/'FAILED').
 */
const PackageCheckout = () => {
  const { state } = useLocation() as { state?: CheckoutState | null };
  const navigate = useNavigate();
  const { t } = useTranslation();
  const purchaseMutation = usePurchasePackage();

  // Kill switch for the cash-on-hand presentation (PR #1481) — still governs
  // whether Cash on Hand is offered at all. Fail-open: unreachable/unconfigured
  // Unleash reads ON. Pay Online is offered independently whenever Stripe is
  // configured (STRIPE_PUBLISHABLE_KEY), matching BookingPage's convention.
  const cashOnHandEnabled = useFeatureFlag(FEATURE_FLAGS.cashOnHandCheckout);
  const onlineAvailable = Boolean(STRIPE_PUBLISHABLE_KEY);

  const tier = state?.tier;
  const serviceId = state?.serviceId;
  const serviceTitle = state?.serviceTitle;
  const isMonthly = tier?.recurrence === 'monthly';
  const selectedAddOns = state?.selectedAddOns || [];
  const sessionAddOns = state?.sessionAddOns || [];

  const [selectedMethod, setSelectedMethod] = useState<SelectedMethod>(
    onlineAvailable ? 'online' : 'cash'
  );
  const [phase, setPhase] = useState<Phase>('review');
  const [createdPackage, setCreatedPackage] = useState<CustomerPackage | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState('');
  const pollCancelRef = useRef(false);

  useEffect(
    () => () => {
      pollCancelRef.current = true;
    },
    []
  );

  if (!tier || !serviceId) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <Card className="border border-gray-200">
          <CardBody className="py-12 text-center space-y-3">
            <p className="text-body text-gray-600">
              {t('package.checkout_missing', 'No package selected.')}
            </p>
            <div className="flex justify-center">
              <CustomButton
                label={t('package.back_to_packages', 'Back to Packages')}
                color="primary"
                variant="flat"
                size="sm"
                onPress={() => navigate(-1)}
              />
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  const tierPrice = Number(tier.price) || 0;
  const addonSubtotal =
    state?.addonSubtotal ??
    selectedAddOns.reduce(
      (sum, addon) =>
        sum + (addon.price || 0) * (addon.sessionNumbers?.length || 0),
      0
    );
  const subtotal = tierPrice + addonSubtotal;
  const taxPlaceholder = 0; // TODO(payment): compute tax once package tax rules land
  const totalDue = subtotal + taxPlaceholder;

  // ── Poll GET /customer-package/customer until the just-created package's
  // paymentStatus resolves. The webhook-driven capture flip
  // (applyPackagePaymentStatus) happens async server-side, so this page can't
  // just trust the create-intent response — it has to watch for the terminal
  // state the same way BookingPage polls booking status after authorization.
  const pollForPaymentResolution = (customerPackageId: string) => {
    const startedAt = Date.now();
    pollCancelRef.current = false;

    const pollOnce = async () => {
      if (pollCancelRef.current) return;
      try {
        const packages = await getMyPackages();
        const match = packages.find(
          (p) => p.customerPackageId === customerPackageId
        );
        if (match) {
          if (match.paymentStatus === 'PAID') {
            setCreatedPackage(match);
            setPhase('confirmed');
            return;
          }
          if (match.paymentStatus === 'FAILED') {
            setErrorMessage(
              t(
                'package.payment_declined',
                'Your card was declined. You can try again.'
              )
            );
            setPhase('failed');
            return;
          }
        }
      } catch (err) {
        // Transient poll failure — keep trying until the timeout below.
      }

      if (pollCancelRef.current) return;
      if (Date.now() - startedAt >= CONFIRM_TIMEOUT_MS) {
        setErrorMessage(
          t(
            'package.payment_confirmation_pending',
            "We're still confirming your payment. Check My Packages shortly."
          )
        );
        setPhase('failed');
        return;
      }
      setTimeout(pollOnce, CONFIRM_POLL_MS);
    };

    pollOnce();
  };

  const buildPurchasePayload = () => ({
    serviceId,
    // BE resolves the tier by name (tiers are embedded with _id:false, so
    // `tier._id` is always undefined).
    tierId: tier.name,
    autoRenew: isMonthly,
    ...(sessionAddOns.length > 0 ? { sessionAddOns } : {}),
  });

  const handleConfirmCash = () => {
    purchaseMutation.mutate(
      {
        ...buildPurchasePayload(),
        ...(cashOnHandEnabled
          ? { paymentMethod: PAYMENT_METHOD_CASH_ON_HAND }
          : {}),
      },
      {
        onSuccess: () => {
          navigate(all_routes.customerPackages);
        },
      }
    );
  };

  const handleConfirmOnline = async () => {
    setErrorMessage('');
    try {
      const doc = await purchaseMutation.mutateAsync(buildPurchasePayload());
      setCreatedPackage(doc);
      try {
        sessionStorage.setItem(
          PACKAGE_PAYMENT_RESUME_KEY,
          JSON.stringify({ customerPackageId: doc.customerPackageId })
        );
      } catch {
        /* sessionStorage unavailable — the redirect can't resume */
      }
      setPhase('paying');
    } catch (err) {
      // purchaseMutation's own onError already surfaces a toast.
    }
  };

  const handleConfirmClick = () => {
    if (selectedMethod === 'online') {
      void handleConfirmOnline();
    } else {
      handleConfirmCash();
    }
  };

  const handleAuthorized = () => {
    try {
      sessionStorage.removeItem(PACKAGE_PAYMENT_RESUME_KEY);
    } catch {
      /* noop */
    }
    if (createdPackage?.customerPackageId) {
      setPhase('confirming');
      pollForPaymentResolution(createdPackage.customerPackageId);
    }
  };

  const handleRetryPayment = () => {
    setErrorMessage('');
    setPhase('paying');
  };

  const isBusy =
    purchaseMutation.isPending || phase === 'paying' || phase === 'confirming';

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate(-1)}
        disabled={isBusy}
        className="flex items-center gap-2 text-body text-gray-500 hover:text-gray-800 mb-4 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FaArrowLeft size={14} /> {t('package.back', 'Back')}
      </button>

      <div className="mb-6">
        <h1 className="text-title2 font-bold text-gray-900">
          {t('package.checkout_title', 'Checkout')}
        </h1>
        <p className="text-body text-gray-500 mt-1">
          {serviceTitle ||
            t('package.checkout_subtitle', 'Review your package.')}
        </p>
      </div>

      <div className="grid grid-cols-1 laptop:grid-cols-5 gap-5">
        {/* ── Left: payment method ───────────────────────────────────── */}
        <div className="laptop:col-span-3 space-y-4">
          <Card shadow="sm" className="border border-gray-200">
            <CardHeader className="border-b border-gray-100 px-5 py-4">
              <p className="text-subtitle3 font-semibold text-gray-800">
                {t('package.payment_method', 'Payment Method')}
              </p>
            </CardHeader>
            <CardBody className="px-5 py-4">
              <div className="flex flex-col gap-3">
                {onlineAvailable && (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => setSelectedMethod('online')}
                    className={`relative flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-colors disabled:cursor-not-allowed ${
                      selectedMethod === 'online'
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <FaCreditCard
                      size={18}
                      className={
                        selectedMethod === 'online'
                          ? 'text-primary shrink-0'
                          : 'text-gray-400 shrink-0'
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-body font-medium text-gray-800">
                        {t('package.pay_online', 'Pay Online')}
                      </p>
                      <p className="text-caption text-gray-500 mt-0.5">
                        {t(
                          'package.pay_online_desc',
                          'Pay securely by card now.'
                        )}
                      </p>
                    </div>
                    {selectedMethod === 'online' && (
                      <span className="h-5 w-5 shrink-0 rounded-full flex items-center justify-center bg-primary">
                        <FaCheck size={10} className="text-white" />
                      </span>
                    )}
                  </button>
                )}

                {cashOnHandEnabled && (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => setSelectedMethod('cash')}
                    className={`relative flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-colors disabled:cursor-not-allowed ${
                      selectedMethod === 'cash'
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <FaMoneyBillWave
                      size={18}
                      className={
                        selectedMethod === 'cash'
                          ? 'text-primary shrink-0'
                          : 'text-gray-400 shrink-0'
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-body font-medium text-gray-800">
                        {t('package.cash_on_hand', 'Cash on Hand')}
                      </p>
                      <p className="text-caption text-gray-500 mt-0.5">
                        {t(
                          'package.cash_on_hand_desc',
                          'Pay the provider in cash at the time of service. No online payment required.'
                        )}
                      </p>
                    </div>
                    {selectedMethod === 'cash' && (
                      <span className="h-5 w-5 shrink-0 rounded-full flex items-center justify-center bg-primary">
                        <FaCheck size={10} className="text-white" />
                      </span>
                    )}
                  </button>
                )}
              </div>

              {/* Card entry — shown once the package is created and the
                  customer chose Pay Online. Stripe's PaymentElement iframe
                  collects the card; this component never sees it. */}
              {selectedMethod === 'online' &&
                (phase === 'paying' ||
                  phase === 'confirming' ||
                  phase === 'failed') &&
                createdPackage?.customerPackageId && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    {phase === 'paying' && (
                      <PaymentSection
                        paymentType="package"
                        bookingId={createdPackage.customerPackageId}
                        serviceId={serviceId}
                        amount={totalDue}
                        returnUrl={RETURN_URL}
                        onAuthorized={handleAuthorized}
                      />
                    )}
                    {phase === 'confirming' && (
                      <div className="flex flex-col items-center gap-2 py-2">
                        <SmallLoadingSpinner size="sm" />
                        <p className="text-caption text-gray-500">
                          {t(
                            'package.confirming_payment',
                            'Confirming your payment…'
                          )}
                        </p>
                      </div>
                    )}
                    {phase === 'failed' && (
                      <div className="flex flex-col items-center gap-2 py-2">
                        <p
                          className="text-xs text-red-500 text-center"
                          role="alert"
                        >
                          {errorMessage}
                        </p>
                        <CustomButton
                          label={t('package.payment_retry', 'Try again')}
                          color="primary"
                          size="sm"
                          onPress={handleRetryPayment}
                        />
                      </div>
                    )}
                  </div>
                )}
            </CardBody>
          </Card>

          <Card shadow="sm" className="border border-gray-200">
            <CardHeader className="border-b border-gray-100 px-5 py-4">
              <p className="text-subtitle3 font-semibold text-gray-800">
                {t('package.billing', 'Billing')}
              </p>
            </CardHeader>
            <CardBody className="px-5 py-4">
              {isMonthly ? (
                <ul className="list-disc pl-5 space-y-1 text-body text-gray-600">
                  <li>
                    {t(
                      'package.billing_monthly_1',
                      'Appointments are valid for the stated period from your purchase date.'
                    )}
                  </li>
                  <li>
                    {t(
                      'package.billing_monthly_3',
                      'This does not renew automatically — you will need to purchase again once your appointments or validity period run out.'
                    )}
                  </li>
                </ul>
              ) : (
                <p className="text-body text-gray-600">
                  {t(
                    'package.billing_block',
                    'One-time payment. Your appointments do not auto-renew.'
                  )}
                </p>
              )}
            </CardBody>
          </Card>
        </div>

        {/* ── Right: order summary ───────────────────────────────────── */}
        <div className="laptop:col-span-2">
          <Card shadow="sm" className="border border-gray-200 sticky top-4">
            <CardHeader className="border-b border-gray-100 px-5 py-4">
              <p className="text-subtitle3 font-semibold text-gray-800">
                {t('package.order_summary', 'Order Summary')}
              </p>
            </CardHeader>
            <CardBody className="px-5 py-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-body font-medium text-gray-800">
                    {tier.name || 'Package'}
                  </p>
                  <p className="text-caption text-gray-400">
                    {tier.sessionCount}{' '}
                    {tier.sessionCount === 1 ? 'appointment' : 'appointments'}
                  </p>
                </div>
                {isMonthly && (
                  <Chip size="sm" radius="full" color="primary" variant="flat">
                    Monthly
                  </Chip>
                )}
              </div>

              {tier.description ? (
                <p className="text-caption text-gray-500">{tier.description}</p>
              ) : null}

              <div className="flex justify-between text-body text-gray-600">
                <span>{t('package.tier_price', 'Package price')}</span>
                <span>{formatCurrency(tierPrice)}</span>
              </div>

              {/* Additional-services breakdown — one line per selected addon,
                  showing exactly which sessions it was assigned to so the
                  per-session price math (price × session count) is
                  transparent rather than a single opaque add-on total. */}
              {selectedAddOns.length > 0 && (
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 space-y-2">
                  <p className="text-caption font-semibold text-gray-600">
                    {t('package.additional_services', 'Additional Services')}
                  </p>
                  {selectedAddOns.map((addon) => (
                    <div
                      key={addon.additionalServiceId}
                      className="flex items-start justify-between gap-2 text-caption text-gray-600"
                    >
                      <span className="min-w-0">
                        {addon.serviceItem}
                        <span className="text-gray-400">
                          {' '}
                          ({formatCurrency(addon.price)} ×{' '}
                          {addon.sessionNumbers.length} appointment
                          {addon.sessionNumbers.length > 1 ? 's' : ''} —
                          Appointment
                          {addon.sessionNumbers.length > 1 ? 's' : ''}{' '}
                          {addon.sessionNumbers.join(', ')})
                        </span>
                      </span>
                      <span className="font-medium text-gray-700 shrink-0">
                        {formatCurrency(
                          addon.price * addon.sessionNumbers.length
                        )}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between text-caption font-semibold text-gray-700 pt-1 border-t border-gray-200">
                    <span>
                      {t('package.addon_subtotal', 'Add-ons subtotal')}
                    </span>
                    <span>{formatCurrency(addonSubtotal)}</span>
                  </div>
                </div>
              )}

              <Divider />

              <div className="flex justify-between text-body text-gray-600">
                <span>{t('package.subtotal', 'Subtotal')}</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-body text-gray-600">
                <span>{t('package.tax', 'Tax')}</span>
                <span>{formatCurrency(taxPlaceholder)}</span>
              </div>
              <Divider />
              <div className="flex justify-between items-baseline">
                <span className="text-body font-medium text-gray-800">
                  {t('package.total', 'Total')}
                </span>
                <span className="text-title1 font-bold text-gray-900">
                  {formatCurrency(totalDue)}
                </span>
              </div>

              {isMonthly && tier.validityDays ? (
                <p className="text-caption text-gray-400">
                  {t('package.validity', 'Valid')} {tier.validityDays}{' '}
                  {t('package.days', 'days')}
                </p>
              ) : null}
            </CardBody>

            {phase === 'review' && (
              <CardFooter className="px-5 py-4 flex flex-col gap-3 border-t border-gray-100">
                <CustomButton
                  label={
                    selectedMethod === 'online'
                      ? t(
                          'package.continue_checkout',
                          'Continue to Secure Checkout'
                        )
                      : t('package.confirm_purchase', 'Confirm Purchase')
                  }
                  color="primary"
                  size="md"
                  fullWidth
                  isLoading={purchaseMutation.isPending}
                  isDisabled={purchaseMutation.isPending}
                  onPress={handleConfirmClick}
                />
                {selectedMethod === 'cash' ? (
                  <p className="text-caption text-gray-400 text-center">
                    {t(
                      'package.cash_footer_note',
                      'You will pay the provider in cash when your service is delivered.'
                    )}
                  </p>
                ) : (
                  <p className="text-caption text-gray-400 text-center">
                    {t(
                      'package.online_footer_note',
                      'Your card is charged once — securely, by Stripe.'
                    )}
                  </p>
                )}
              </CardFooter>
            )}

            {phase === 'confirmed' && (
              <CardFooter className="px-5 py-4 flex flex-col gap-3 border-t border-gray-100">
                <CustomAlert
                  title={t(
                    'package.purchase_success_title',
                    'Payment successful'
                  )}
                  description={t(
                    'package.purchase_success_desc',
                    'Your package is ready to use.'
                  )}
                  color="success"
                />
                <CustomButton
                  label={t('package.view_packages', 'View My Packages')}
                  color="primary"
                  size="md"
                  fullWidth
                  onPress={() => navigate(all_routes.customerPackages)}
                />
              </CardFooter>
            )}
          </Card>

          {purchaseMutation.isError && phase === 'review' && (
            <div className="mt-4">
              <CustomAlert
                title={t('package.purchase_error_title', 'Purchase failed')}
                description={
                  (purchaseMutation.error as any)?.message ||
                  t(
                    'package.purchase_error_desc',
                    'We could not create your package. Please try again.'
                  )
                }
                color="danger"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackageCheckout;
